// Material UI Imports
import {
  Autocomplete,
  InputLabel,
  Popper,
  TextField,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  autocompleteClasses,
} from "@mui/material/Autocomplete";

// Type Imports
import { ISelectOption } from "@pulse-ui/types/components/DropdownInput";

const StyledListItem = styled("li")({
  backgroundColor: "#0c1835",
  color: "#e8eaef",
  "&:hover": {
    backgroundColor: "#132758",
  },
});

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.paper}`]: {
    backgroundColor: "#0c1835",
    boxShadow: "5px 5px 5px #091229",
    border: "0.5px solid #091229",
  },
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: "border-box",
    "& ul": {
      padding: 0,
      margin: 0,
    },
  },
});

const AutocompleteInput = ({
  value,
  inputLabel,
  disabled,
  options,
  onChange,
  labelInside = false,
  width,
  containerClassNames,
}: {
  value: ISelectOption;
  inputLabel: string;
  disabled: boolean;
  options: ISelectOption[];
  onChange: (
    event: React.SyntheticEvent,
    newValue: ISelectOption | null,
    reason: AutocompleteChangeReason,
    details?: AutocompleteChangeDetails<ISelectOption> | undefined
  ) => void;
  labelInside?: boolean;
  width?: number;
  containerClassNames?: string;
}) => {
  return (
    <div
      className={`autocomplete-input-container ${labelInside ? "autocomplete-input-container-label-inside" : ""
        } ${containerClassNames}`}
      style={width ? { minWidth: width } : undefined}
    >
      {!labelInside && (
        <InputLabel
          className={`dropdown-input-select-label ${disabled && "dropdown-input-select-label-disabled"
            }`}
          disabled={disabled}
        >
          {inputLabel}
        </InputLabel>
      )}
      <Autocomplete
        value={value}
        options={options}
        getOptionLabel={(option) => option.label}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={labelInside ? "standard" : "outlined"}
            label={labelInside ? inputLabel : undefined}
            sx={
              labelInside
                ? {
                  "& .MuiInputBase-root": {
                    color: "#e8eaef",
                    fontFamily: "Lato, sans-serif",
                    fontSize: "16px",
                    fontWeight: 400,
                    "&::before": {
                      borderColor: "#b6bdce",
                    },
                    "&:hover:before": {
                      borderColor: "#b6bdce",
                    },
                    "&::after": {
                      borderColor: "#b6bdce",
                    },
                  },
                  "& .MuiInputLabel-root": {
                    color: "#b6bdce",
                    fontFamily: "Lato, sans-serif",
                    fontSize: "16px",
                    fontWeight: 400,
                  },
                  "& .Mui-disabled": {
                    color: "#8a8f9d !important",
                    "&::before": {
                      border: "none",
                    },
                    "&:hover:before": {
                      border: "none",
                    },
                  },
                }
                : undefined
            }
            slotProps={{
              inputLabel: labelInside
                ? {
                  className: `dropdown-input-select-label ${disabled && "dropdown-input-select-label-disabled"
                    }`,
                  disabled: disabled,
                }
                : undefined
            }}
          />
        )}
        onChange={onChange}
        disabled={disabled}
        renderOption={(props, option) => (
          <StyledListItem {...props}>{option.label}</StyledListItem>
        )}
        isOptionEqualToValue={(option: any, value: any) =>
          option.value === value.value
        }
        getOptionDisabled={(option) => option.disabled || false}
        slots={{
          popper: StyledPopper
        }}
      />
    </div>
  );
};

export default AutocompleteInput;